import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useProjectNavigation } from "../../../hooks/useProjectNavigation";
import { useTranslation } from "react-i18next";
import { ParticipantsEditor } from "../views/ParticipantsEditor";
import { RundownEditor } from "../views/RundownEditor";
import { Dashboard } from "../views/Dashboard";
import { StreamFeed } from "../views/StreamFeed";
import { TagsEditor } from "../views/TagsEditor";
import { BanksEditor } from "../views/BanksEditor";
import { GroupsEditor } from "../views/GroupsEditor";

// Lazy-load cross-module views to maintain module boundaries
const Discovery = React.lazy(() =>
  import("../../discovery/views/Discovery").then((m) => ({ default: m.Discovery })),
);
const StoryProducer = React.lazy(() =>
  import("../../storyproducer/views/StoryProducer").then((m) => ({ default: m.StoryProducer })),
);
const AssemblyManager = React.lazy(() => import("../../storyproducer/components/AssemblyManager"));
import { FullscreenButton } from "../../common/components/FullscreenButton";
import { BugReportButton } from "../../common/components/BugReportButton";
import { LanguageToggle } from "../../common/components/LanguageToggle";

import { RoleBrandingHeader } from "../../common/components/RoleBrandingHeader";
import { UserMenu } from "../../common/components/UserMenu";
import { ButtonGroup } from "@/components/ui/button-group";
import { ConnectionStatusWidget } from "../../common/components/ConnectionStatusWidget";
import { Rundown, Story, Participant } from "../../common/types/domain";
import { CustomDialog } from "../../common/components/CustomDialog";
import { useAuth } from "../../auth/contexts/AuthContext";
import { socketService } from "../../../services/socket";
import { SummaryFullView } from "../../executive/views/SummaryFullView";

import { useProjects } from "../../common/contexts/ProjectContext";

export type ControllerMode = "controller" | "contentDirector";

interface ControllerLayoutProps {
  /** 'controller' = Content Controller (no settings, has dashboard).
   *  'contentDirector' = Content Director (has settings+rundowns, no dashboard). */
  mode?: ControllerMode;
}

export const ControllerLayout: React.FC<ControllerLayoutProps> = ({ mode = "controller" }) => {
  const { t } = useTranslation();
  const { navigate } = useProjectNavigation();
  const { user } = useAuth();
  const { activeProject, clearActiveProject } = useProjects();

  const isDirectorMode = mode === "contentDirector";
  const isControllerMode = mode === "controller";

  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = [
    "dashboard",
    "participants",
    "tags",
    "rundowns",
    "roles",
    "banks",
    "groups",
    "discovery",
    "explore",
    "none",
  ] as const;
  type Tab = (typeof VALID_TABS)[number];
  const tabParam = searchParams.get("tab");
  const defaultTab: Tab = isDirectorMode ? "explore" : "dashboard";
  const activeTab: Tab =
    tabParam === null
      ? defaultTab
      : VALID_TABS.includes(tabParam as Tab)
        ? (tabParam as Tab)
        : defaultTab;

  const setActiveTab = (tab: Tab) => {
    if (
      ["dashboard", "participants", "tags", "rundowns", "roles", "banks", "groups"].includes(tab)
    ) {
      setShowDiscover(false);
      setShowAssembly(false);
      setShowVtr(false);
      setShowFeed(false);
    }
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (tab === defaultTab) params.delete("tab");
        else params.set("tab", tab);
        return params;
      },
      { replace: true },
    );
  };

  // BRAIN / CUT / VTR / SUMMARIES / FEED open state — plain local state. Each is a
  // column: in the set → column shows, out → hidden. (Previously persisted in
  // ?panels=, which made a closed panel re-open because a missing param fell back
  // to defaults.)
  const PANEL_KEYS = {
    brain: "brain",
    cut: "cut",
    vtr: "vtr",
    summaries: "summaries",
    feed: "feed",
  } as const;
  const [openPanels, setOpenPanels] = useState<Set<string>>(() => new Set());
  const showDiscover = openPanels.has(PANEL_KEYS.brain);
  const showAssembly = openPanels.has(PANEL_KEYS.cut);
  const showVtr = openPanels.has(PANEL_KEYS.vtr);
  const showSummaries = openPanels.has(PANEL_KEYS.summaries);
  const showFeed = openPanels.has(PANEL_KEYS.feed);

  const updatePanels = (mutator: (panels: Set<string>) => void) => {
    setOpenPanels((prev) => {
      const next = new Set(prev);
      mutator(next);
      return next;
    });
  };
  const togglePanel = (key: string, value: boolean | ((prev: boolean) => boolean)) => {
    updatePanels((p) => {
      const resolved = typeof value === "function" ? value(p.has(key)) : value;
      if (resolved) p.add(key);
      else p.delete(key);
    });
  };
  const setShowDiscover = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel(PANEL_KEYS.brain, v);
  const setShowAssembly = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel(PANEL_KEYS.cut, v);
  const setShowVtr = (v: boolean | ((prev: boolean) => boolean)) => togglePanel(PANEL_KEYS.vtr, v);
  const setShowSummaries = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel(PANEL_KEYS.summaries, v);
  const setShowFeed = (v: boolean | ((prev: boolean) => boolean)) =>
    togglePanel(PANEL_KEYS.feed, v);

  const handleNavigateToLog = (
    logId: string,
    storyId: string,
    _highlightText?: string,
    citationType?: string,
    citationTc?: string,
  ) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set("tab", "explore");
        params.set("storyId", storyId);
        params.set("logId", logId);
        if (citationType) params.set("type", citationType);
        if (citationTc) params.set("tc", citationTc);
        return params;
      },
      { replace: true },
    );
  };

  const [activeRundown, setActiveRundown] = useState<Rundown | null>(null);
  const [rundowns, setRundowns] = useState<Rundown[]>([]);
  const [stories, setStories] = useState<Story[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [participantRoles, setParticipantRoles] = useState<{ id: string; name: string }[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [activeLoggers, setActiveLoggers] = useState<{ id: string; name: string }[]>([]);
  const [banks, setBanks] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [resetStep, setResetStep] = useState<0 | 1 | 2>(0);
  const [isConnected, setIsConnected] = useState(socketService.connected);
  const [isInitializing, setIsInitializing] = useState(true);
  const [eventsCount, setEventsCount] = useState(0);
  const [transcriptsCount, setTranscriptsCount] = useState(0);
  const _lastRequestedProjectId = useRef<string | null>(null);
  const [dialog, setDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "alert" | "confirm";
    onConfirm: (val?: string) => void;
    defaultValue?: string;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "alert",
    onConfirm: () => {},
  });

  const handleFetchStoryLogs = (storyId: string, filters?: any) => {
    socketService.emit("get-story-logs", { storyId, filters }, (logs: any[]) => {
      if (logs && logs.length > 0) {
        setStories((prev: Story[]) => prev.map((s) => (s.id === storyId ? { ...s, logs } : s)));
      }
    });
  };

  useEffect(() => {
    if (!activeProject) return;

    // Debug: track tab sleep/wake cycles
    const onVisibilityChange = () => {
      console.debug(
        `[ControllerLayout] 👁️ Tab ${document.hidden ? "HIDDEN (sleeping)" : "VISIBLE (waking)"}, socket=${socketService.connected}`,
      );
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const onConnect = () => {
      console.debug("[ControllerLayout] ✅ Socket connected");
      setIsConnected(true);
    };
    const onDisconnect = (reason: string) => {
      console.warn(`[ControllerLayout] ❌ Socket disconnected: ${reason}`);
      setIsConnected(false);
    };

    const handleInit = (data: any) => {
      setIsInitializing(false);
      if (!data) return;

      try {
        console.debug(
          `[ControllerLayout] 📦 init-data received: activeRundownId=${data.activeRundownId}, rundowns=${data.rundowns?.length}`,
        );
        if (data.rundowns && Array.isArray(data.rundowns)) setRundowns(data.rundowns);
        if (data.activeRundownId && Array.isArray(data.rundowns)) {
          const active = data.rundowns.find((r: any) => r.id === data.activeRundownId);
          if (active) setActiveRundown(active);
        }
        if (data.stories) setStories(data.stories);
        // Only accept server participants if non-empty to avoid wiping state
        if (data.participants && Array.isArray(data.participants) && data.participants.length > 0)
          setParticipants(data.participants);
        if (data.roles && Array.isArray(data.roles)) setParticipantRoles(data.roles);
        if (data.staff && Array.isArray(data.staff)) setStaff(data.staff);
        if (data.activeLoggers) setActiveLoggers(data.activeLoggers);
        if (data.banks && Array.isArray(data.banks)) setBanks(data.banks);
        if (data.groups && Array.isArray(data.groups)) setGroups(data.groups);
        if (data.eventsCount !== undefined) setEventsCount(data.eventsCount);
        if (data.transcriptsCount !== undefined) setTranscriptsCount(data.transcriptsCount);
      } catch (err) {
        console.error("❌ [ControllerLayout] Error processing init-data:", err);
      }
    };

    const handleRundownItemChange = (data: { id: string | null; rundown: Rundown | null }) => {
      if (data.rundown) {
        setActiveRundown({
          id: data.id!,
          name: data.rundown.name,
          date: data.rundown.date,
          items: data.rundown.items || [],
        });
      } else {
        setActiveRundown(null);
      }
    };

    const handleRundownUpdate = (data: { id: string; items: any[] }) => {
      setActiveRundown((prev: any) =>
        prev && data.id === prev.id ? { ...prev, items: data.items } : prev,
      );
    };

    const handleStoriesUpdate = (data: Story[] | { rundownId: string; stories: Story[] }) => {
      setStories(Array.isArray(data) ? data : data.stories);
    };

    const handleLogAdded = (newLog: any) => {
      setStories((prevStories) =>
        prevStories.map((story) => {
          if (story.id === newLog.storyId) {
            return { ...story, logs: [...(story.logs || []), newLog] };
          }
          return story;
        }),
      );
    };

    const handleParticipantsUpdate = (updated: Participant[]) => {
      // Accept any valid array — this event comes from intentional user actions (add/edit/delete)
      if (Array.isArray(updated)) {
        setParticipants(updated);
      }
    };
    const handleParticipantRolesUpdate = (updated: { id: string; name: string }[]) =>
      setParticipantRoles(updated);
    const handleStaffUpdate = (updated: any[]) => setStaff(updated);
    const handleLoggersUpdate = (data: { id: string; name: string }[]) => setActiveLoggers(data);
    const handleBanksUpdate = (updated: any[]) => setBanks(updated);
    const handleGroupsUpdate = (updated: any[]) => setGroups(updated);

    const handleResetComplete = (res: { success: boolean; error?: string }) => {
      if (res.success) {
        setStories([]);
        setResetStep(0);
      } else {
        setDialog((d) => ({
          ...d,
          isOpen: true,
          title: "System Error",
          message: "Error clearing data: " + res.error,
        }));
        setResetStep(0);
      }
    };

    const identify = () => {
      if (user) {
        socketService.emit("identify-logger", { name: user.name.toUpperCase() });
        socketService.emit("register-editor");
      }
      // Note: select-project is already emitted by ProjectContext on connect/reconnect.
      // Do NOT emit it here to avoid duplicate init-data responses.
    };

    const handleFilteredStories = (data: { stories: Story[] }) => setStories(data.stories);

    socketService.on("connect", onConnect);
    socketService.on("disconnect", onDisconnect);
    socketService.on("connect", identify);
    socketService.on("init-data", handleInit);
    socketService.on("active-rundown-changed", handleRundownItemChange);
    socketService.on("rundown-updated", handleRundownUpdate);
    socketService.on("stories-updated", handleStoriesUpdate);
    socketService.on("log-added", handleLogAdded);
    socketService.on("participants-updated", handleParticipantsUpdate);
    socketService.on("roles-updated", handleParticipantRolesUpdate);
    socketService.on("staff-updated", handleStaffUpdate);
    socketService.on("active-loggers-updated", handleLoggersUpdate);
    socketService.on("filtered-project-stories", handleFilteredStories);
    socketService.on("banks-updated", handleBanksUpdate);
    socketService.on("groups-updated", handleGroupsUpdate);
    socketService.on("RESET_COMPLETE", handleResetComplete);

    socketService.connect();

    if (socketService.connected) {
      onConnect();
      identify();
      // On first navigation, ProjectSlugGuard already emitted select-project
      // *before* this component mounted — so the init-data response was lost.
      // Re-request it now that our listener is registered.
      if (activeProject?.id) {
        socketService.emit("select-project", activeProject.id);
      }
    }

    // Safety timeout: clear loading state after 10s even if init-data never arrives
    const safetyTimeout = setTimeout(() => {
      setIsInitializing(false);
    }, 10_000);

    return () => {
      clearTimeout(safetyTimeout);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      socketService.off("connect", onConnect);
      socketService.off("disconnect", onDisconnect);
      socketService.off("connect", identify);
      socketService.off("init-data", handleInit);
      socketService.off("active-rundown-changed", handleRundownItemChange);
      socketService.off("rundown-updated", handleRundownUpdate);
      socketService.off("stories-updated", handleStoriesUpdate);
      socketService.off("log-added", handleLogAdded);
      socketService.off("participants-updated", handleParticipantsUpdate);
      socketService.off("roles-updated", handleParticipantRolesUpdate);
      socketService.off("staff-updated", handleStaffUpdate);
      socketService.off("active-loggers-updated", handleLoggersUpdate);
      socketService.off("filtered-project-stories", handleFilteredStories);
      socketService.off("banks-updated", handleBanksUpdate);
      socketService.off("groups-updated", handleGroupsUpdate);
      socketService.off("RESET_COMPLETE", handleResetComplete);
    };
  }, [activeProject, user]);

  const ACCENT_COLOR = isDirectorMode
    ? "var(--color-role-contentdirector)"
    : "var(--color-role-controller)";
  const [showSettings, setShowSettings] = useState(false);

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        background: "var(--color-bg-dark)",
        color: "var(--color-text-primary)",
        overflow: "hidden",
      }}
      data-testid="controller-layout"
    >
      {/* HEADER - 3-COLUMN LAYOUT (Story Producer Style) */}
      <header
        data-testid="controller-header"
        style={{
          height: "88px",
          boxSizing: "border-box",
          padding: "0 min(40px, 3vw)",
          borderBottom: `5px solid ${ACCENT_COLOR}`,
          borderTop: "none",
          borderLeft: "none",
          borderRight: "none",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          background: "var(--color-panel-bg)",
          backdropFilter: "blur(10px)",
          boxShadow: `0 0 20px ${ACCENT_COLOR}15`,
          zIndex: 100,
          position: "relative",
        }}
      >
        {/* LEFT: Logo & Breadcrumb */}
        <div
          data-testid="controller-header-left"
          style={{ display: "flex", alignItems: "center", flex: 1 }}
        >
          <RoleBrandingHeader
            role={isDirectorMode ? "contentdirector" : "controller"}
            roleLabel={
              isDirectorMode
                ? t("auth.roles.contentDirector", "CONTENT DIRECTOR").toUpperCase()
                : t("controller.layout.contentController").toUpperCase()
            }
            projectName={activeProject?.name}
            onHomeClick={() => {
              clearActiveProject();
              navigate("/projects");
            }}
            testIdPrefix={isDirectorMode ? "contentdirector" : "controller"}
          />
        </div>

        {/* CENTER: Navigation Buttons */}
        <div
          data-testid="controller-nav"
          style={{ position: "absolute", left: "50%", transform: "translateX(-50%)" }}
        >
          <div
            data-testid="controller-nav-tabs"
            style={{
              display: "flex",
              gap: "8px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "12px",
              padding: "6px",
              border: "1px solid rgba(255,255,255,0.05)",
            }}
          >
            {/* DISCOVER Toggle */}
            <button
              data-testid="controller-toggle-discover"
              onClick={() => {
                setShowDiscover((prev) => !prev);
                if (
                  [
                    "dashboard",
                    "participants",
                    "tags",
                    "rundowns",
                    "roles",
                    "banks",
                    "groups",
                  ].includes(activeTab)
                ) {
                  setActiveTab("none");
                }
              }}
              style={{
                padding: "8px 20px",
                background: showDiscover ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showDiscover ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-brain" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.discover")}
            </button>

            {/* EXPLORE (StoryProducer) */}
            <button
              data-testid="nav-tab-explore"
              onClick={() => setActiveTab(activeTab === "explore" ? "none" : "explore")}
              style={{
                padding: "8px 20px",
                background: activeTab === "explore" ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: activeTab === "explore" ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-compass" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.explore")}
            </button>

            {/* CUT Toggle */}
            <button
              data-testid="controller-toggle-cut"
              onClick={() => {
                const next = !showAssembly;
                setShowAssembly(next);
                if (
                  [
                    "dashboard",
                    "participants",
                    "tags",
                    "rundowns",
                    "roles",
                    "banks",
                    "groups",
                  ].includes(activeTab)
                ) {
                  setActiveTab("none");
                }
              }}
              style={{
                padding: "8px 20px",
                background: showAssembly ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showAssembly ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-movie" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.cut")}
            </button>

            {/* VTR Toggle */}
            <button
              data-testid="controller-toggle-vtr"
              onClick={() => {
                const next = !showVtr;
                setShowVtr(next);
                if (
                  [
                    "dashboard",
                    "participants",
                    "tags",
                    "rundowns",
                    "roles",
                    "banks",
                    "groups",
                  ].includes(activeTab)
                ) {
                  setActiveTab("none");
                }
              }}
              style={{
                padding: "8px 20px",
                background: showVtr ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showVtr ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-check-shield" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.approved", "VTR")}
            </button>

            {/* SUMMARIES Toggle */}
            <button
              data-testid="controller-toggle-summaries"
              onClick={() => {
                setShowSummaries((prev) => !prev);
                if (
                  [
                    "dashboard",
                    "participants",
                    "tags",
                    "rundowns",
                    "roles",
                    "banks",
                    "groups",
                  ].includes(activeTab)
                ) {
                  setActiveTab("none");
                }
              }}
              style={{
                padding: "8px 20px",
                background: showSummaries ? ACCENT_COLOR : "transparent",
                border: "none",
                borderRadius: "8px",
                color: showSummaries ? "#fff" : "#888",
                cursor: "pointer",
                fontSize: "0.85rem",
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "all 0.2s",
              }}
            >
              <i className="bx bx-bar-chart-alt-2" style={{ fontSize: "1.2rem" }}></i>
              {t("controller.layout.summaries", "SUMMARIES")}
            </button>

            {/* DASHBOARD (Controller) / FEED panel-toggle (Director) */}
            {isDirectorMode ? (
              <button
                data-testid="controller-toggle-feed"
                onClick={() => {
                  setShowFeed((prev) => !prev);
                  if (
                    [
                      "dashboard",
                      "participants",
                      "tags",
                      "rundowns",
                      "roles",
                      "banks",
                      "groups",
                    ].includes(activeTab)
                  ) {
                    setActiveTab("none");
                  }
                }}
                style={{
                  padding: "8px 20px",
                  background: showFeed ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: showFeed ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <span
                  className="blink"
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "var(--color-accent-danger, #f72d60)",
                    boxShadow: "0 0 6px var(--color-accent-danger, #f72d60)",
                    display: "inline-block",
                  }}
                />
                {t("controller.layout.feed", "FEED")}
              </button>
            ) : (
              <button
                data-testid="nav-tab-dashboard"
                onClick={() => setActiveTab(activeTab === "dashboard" ? "none" : "dashboard")}
                style={{
                  padding: "8px 20px",
                  background: activeTab === "dashboard" ? ACCENT_COLOR : "transparent",
                  border: "none",
                  borderRadius: "8px",
                  color: activeTab === "dashboard" ? "#fff" : "#888",
                  cursor: "pointer",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s",
                }}
              >
                <i className="bx bx-grid-alt" style={{ fontSize: "1.2rem" }}></i>
                {t("controller.layout.dashboard")}
              </button>
            )}

            {/* SETTINGS (Dropdown) - Hidden in Controller mode */}
            {!isControllerMode && (
              <div data-testid="nav-settings-dropdown" style={{ position: "relative" }}>
                <button
                  data-testid="nav-tab-settings"
                  onClick={() => setShowSettings(!showSettings)}
                  onBlur={() => setTimeout(() => setShowSettings(false), 200)}
                  style={{
                    padding: "8px 20px",
                    background: [
                      "participants",
                      "tags",
                      "rundowns",
                      "groups",
                      "banks",
                      "roles",
                    ].includes(activeTab)
                      ? ACCENT_COLOR
                      : "transparent",
                    border: "none",
                    borderRadius: "8px",
                    color: [
                      "participants",
                      "tags",
                      "rundowns",
                      "groups",
                      "banks",
                      "roles",
                    ].includes(activeTab)
                      ? "#fff"
                      : "#888",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    transition: "all 0.2s",
                  }}
                >
                  <i className="bx bx-cog" style={{ fontSize: "1.2rem" }}></i>
                  {t("controller.layout.settings")}{" "}
                  <i className={`bx bx-chevron-${showSettings ? "up" : "down"}`}></i>
                </button>

                {showSettings && (
                  <div
                    style={{
                      position: "absolute",
                      top: "120%",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "min(180px, 40vw)",
                      background: "var(--color-panel-bg)",
                      border: "1px solid var(--color-panel-border)",
                      borderRadius: "8px",
                      padding: "8px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.5)",
                      zIndex: 100,
                    }}
                  >
                    {[
                      {
                        id: "participants",
                        label: t("controller.layout.participants"),
                        icon: "bx-user",
                      },
                      { id: "tags", label: t("controller.layout.tags"), icon: "bx-tag" },
                      {
                        id: "rundowns",
                        label: t("controller.layout.rundowns"),
                        icon: "bx-list-ul",
                        color: "var(--color-type-rundown)",
                      },
                    ].map((item) => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveTab(item.id as any);
                          setShowSettings(false);
                        }}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          background:
                            activeTab === item.id ? "var(--color-selection-bg)" : "transparent",
                          color:
                            activeTab === item.id ? (item as any).color || ACCENT_COLOR : "#ccc",
                          border: "none",
                          borderLeft:
                            activeTab === item.id && (item as any).color
                              ? `3px solid ${(item as any).color}`
                              : "3px solid transparent",
                          borderRadius: "6px",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = "rgba(255,255,255,0.05)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background =
                            activeTab === item.id ? "var(--color-selection-bg)" : "transparent")
                        }
                        onMouseDown={(e) => e.preventDefault()}
                      >
                        <i
                          className={`bx ${item.icon}`}
                          style={{ fontSize: "1rem", color: (item as any).color || "inherit" }}
                        ></i>
                        {item.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: User & Logout */}
        <div
          data-testid="controller-header-right"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            justifyContent: "flex-end",
            flex: 1,
          }}
        >
          <ButtonGroup>
            <LanguageToggle />
            <BugReportButton module="controller" />
            <FullscreenButton isInline={true} />
          </ButtonGroup>

          <ConnectionStatusWidget />

          <UserMenu
            role={isDirectorMode ? "contentdirector" : "controller"}
            testIdPrefix={isDirectorMode ? "contentdirector-user" : "controller-user"}
          />
        </div>
      </header>

      {/* MAIN CONTENT AREA */}
      <div
        data-testid="controller-content"
        style={
          {
            flex: 1,
            display: "flex",
            overflow: "hidden",
            padding: "20px",
            gap: "20px",
            // Force role accent color for sub-modules
            "--color-accent-primary": ACCENT_COLOR,
            "--color-accent-secondary": ACCENT_COLOR,
            "--color-btn-primary": ACCENT_COLOR,
            "--color-btn-hover": isDirectorMode
              ? "rgba(139, 92, 246, 0.1)" // #8B5CF6 @ 10%
              : "rgba(56, 189, 248, 0.1)", // #38BDF8 @ 10%
            "--color-highlight": isDirectorMode
              ? "rgba(139, 92, 246, 0.2)" // #8B5CF6 @ 20%
              : "rgba(56, 189, 248, 0.2)", // #38BDF8 @ 20%
            "--color-role-sp": ACCENT_COLOR, // Override Story Producer Role Color
            "--color-brand": ACCENT_COLOR, // Override Brand Color
            "--discovery-accent": ACCENT_COLOR, // Explicitly override Discovery Accent
          } as React.CSSProperties
        }
      >
        {/* Panel LEFT: DISCOVERY */}
        <div
          data-testid="controller-discover-panel"
          style={{
            flex: showDiscover ? 1 : 0,
            width: showDiscover ? "auto" : 0,
            minWidth: showDiscover ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showDiscover ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showDiscover ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showDiscover ? "auto" : "none",
          }}
        >
          {showDiscover && (
            <React.Suspense fallback={null}>
              <Discovery
                onNavigateToLog={handleNavigateToLog}
                hideAiDetails={user?.role !== "Superadmin"}
              />
            </React.Suspense>
          )}
        </div>

        {/* Panel LEFT: FEED (live stream) */}
        <div
          data-testid="controller-feed-panel"
          style={{
            flex: showFeed ? 1 : 0,
            width: showFeed ? "auto" : 0,
            minWidth: showFeed ? "360px" : 0,
            height: "100%",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showFeed ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showFeed ? "auto" : "none",
          }}
        >
          {showFeed && <StreamFeed externalStories={stories} externalParticipants={participants} />}
        </div>

        {/* Panel CENTER: ACTIVE TAB CONTENT */}
        <div
          style={{
            flex: activeTab !== "none" ? 1 : "0 0 0",
            width: activeTab !== "none" ? "auto" : 0,
            display: "flex",
            flexDirection: "column",
            background: "var(--color-panel-bg)",
            border: activeTab !== "none" ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            overflow: "hidden",
            opacity: activeTab !== "none" ? 1 : 0,
            pointerEvents: activeTab !== "none" ? "auto" : "none",
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
          }}
        >
          {activeTab === "dashboard" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <Dashboard
                externalActiveRundown={activeRundown}
                externalAllRundowns={rundowns}
                externalStories={stories}
                externalParticipants={participants}
                externalActiveLoggers={activeLoggers}
                externalStaff={staff}
                isConnected={isConnected}
                eventsCount={eventsCount}
                transcriptsCount={transcriptsCount}
                onFetchStoryLogs={handleFetchStoryLogs}
                isLoading={isInitializing}
              />
            </div>
          )}
          {activeTab === "participants" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <ParticipantsEditor
                externalParticipants={participants}
                externalRoles={participantRoles}
                externalGroups={groups}
              />
            </div>
          )}
          {activeTab === "groups" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <GroupsEditor externalGroups={groups} externalParticipants={participants} />
            </div>
          )}
          {activeTab === "tags" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <TagsEditor />
            </div>
          )}
          {activeTab === "rundowns" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <RundownEditor readOnly={isDirectorMode} />
            </div>
          )}
          {activeTab === "banks" && (
            <div style={{ flex: 1, overflowY: "auto" }}>
              <BanksEditor
                externalStaff={staff}
                externalParticipants={participants}
                externalBanks={banks}
                externalGroups={groups}
              />
            </div>
          )}
          {activeTab === "explore" && (
            <div style={{ flex: 1, overflow: "hidden" }}>
              <React.Suspense
                fallback={
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "rgba(255,255,255,0.3)",
                    }}
                  >
                    Loading...
                  </div>
                }
              >
                <StoryProducer isEmbedded={true} externalShowAssembly={showAssembly} />
              </React.Suspense>
            </div>
          )}
        </div>

        {/* Panel RIGHT: ASSEMBLY (CUT mode) */}
        <div
          data-testid="controller-assembly-panel"
          style={{
            flex: showAssembly ? 1 : 0,
            width: showAssembly ? "auto" : 0,
            minWidth: showAssembly ? "380px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showAssembly ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showAssembly ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showAssembly ? "auto" : "none",
          }}
        >
          {showAssembly && (
            <React.Suspense fallback={null}>
              <AssemblyManager />
            </React.Suspense>
          )}
        </div>

        {/* Panel RIGHT: VTR (approved-only mode) */}
        <div
          data-testid="controller-vtr-panel"
          style={{
            flex: showVtr ? 1 : 0,
            width: showVtr ? "auto" : 0,
            minWidth: showVtr ? "380px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showVtr ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showVtr ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showVtr ? "auto" : "none",
          }}
        >
          {showVtr && (
            <React.Suspense fallback={null}>
              <AssemblyManager showOnlyApproved={true} />
            </React.Suspense>
          )}
        </div>

        {/* Panel RIGHT: SUMMARIES */}
        <div
          data-testid="controller-summaries-panel"
          style={{
            flex: showSummaries ? 1 : 0,
            width: showSummaries ? "auto" : 0,
            minWidth: showSummaries ? "380px" : 0,
            height: "100%",
            overflow: "hidden",
            background: "var(--color-panel-bg)",
            border: showSummaries ? "1px solid var(--color-panel-border)" : "none",
            borderRadius: "var(--border-radius-lg)",
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            opacity: showSummaries ? 1 : 0,
            transition: "flex 0.3s ease, opacity 0.3s ease, width 0.3s ease",
            pointerEvents: showSummaries ? "auto" : "none",
            padding: showSummaries ? "0 24px" : 0,
          }}
        >
          {showSummaries && activeProject?.id && (
            <SummaryFullView projectId={activeProject.id} accentColor={ACCENT_COLOR} />
          )}
        </div>
      </div>

      {/* Reset Confirmation Overlay */}
      {resetStep > 0 && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(247,45,96,0.2)",
            backdropFilter: "blur(5px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
        >
          <div
            style={{
              background: "var(--color-panel-bg)",
              padding: "40px",
              borderRadius: "20px",
              border:
                resetStep === 1
                  ? "2px solid var(--color-accent-warning)"
                  : "2px solid var(--color-accent-danger)",
              textAlign: "center",
              maxWidth: "min(450px, 90vw)",
              boxShadow:
                resetStep === 1
                  ? "0 0 30px rgba(255, 204, 0, 0.2)"
                  : "0 0 40px rgba(247, 45, 96, 0.4)",
            }}
          >
            <div style={{ fontSize: "3rem", marginBottom: "10px" }}>
              {resetStep === 1 ? "⚠️" : "🚨"}
            </div>
            <h2
              style={{
                color:
                  resetStep === 1 ? "var(--color-accent-warning)" : "var(--color-accent-danger)",
                marginBottom: "20px",
                fontSize: "1.8rem",
                fontWeight: "900",
              }}
            >
              {resetStep === 1
                ? t("controller.layout.resetSession")
                : t("controller.layout.confirmArchive")}
            </h2>
            <p
              style={{
                fontSize: "1.1rem",
                marginBottom: "30px",
                color: "var(--color-text-secondary)",
                lineHeight: "1.5",
              }}
            >
              {resetStep === 1
                ? t("controller.layout.resetWarning")
                : t("controller.layout.resetNote")}
            </p>
            <div style={{ display: "flex", gap: "15px", justifyContent: "center" }}>
              <button
                onClick={() => setResetStep(0)}
                style={{
                  padding: "12px 25px",
                  borderRadius: "8px",
                  border: "1px solid var(--color-panel-border)",
                  background: "transparent",
                  color: "rgba(255, 255, 255, 0.8)",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                {t("controller.layout.cancel")}
              </button>
              <button
                onClick={() => {
                  if (resetStep === 1) {
                    setResetStep(2);
                  } else {
                    socketService.emit("delete-all-data");
                  }
                }}
                style={{
                  padding: "12px 25px",
                  borderRadius: "8px",
                  border: "none",
                  background:
                    resetStep === 1 ? "var(--color-accent-warning)" : "var(--color-accent-danger)",
                  color: resetStep === 1 ? "#000" : "#fff",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                {resetStep === 1
                  ? t("controller.layout.yesContinue")
                  : t("controller.layout.archiveClear")}
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomDialog
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
        defaultValue={dialog.defaultValue}
        onConfirm={dialog.onConfirm}
        onCancel={() => setDialog((d) => ({ ...d, isOpen: false }))}
      />
    </div>
  );
};
